// src/pages/Portfolio.js
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Code2,
  Globe,
  Smartphone,
  ShoppingCart,
  Briefcase,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils/createPageUrl";
import "../styles/portfolio.css";

const CATEGORY_COLORS = {
  corporate: "#FF8A3C",
  ecommerce: "#2D8CFF",
  mobile: "#10B981",
  webapp: "#8B5CF6",
  all: "#080808ff",
};

const getCategoryColor = (categoryId) =>
  CATEGORY_COLORS[categoryId] || "#2D8CFF";

// ✅ Supporte categoryIds (tableau) ou categoryId (string)
const getProjectCategoryIds = (project) => {
  if (Array.isArray(project.categoryIds) && project.categoryIds.length > 0) {
    return project.categoryIds;
  }
  if (project.categoryId) {
    return [project.categoryId];
  }
  return [];
};

function Portfolio() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState("all");

  const categories =
    t("portfolio.filters.categories", { returnObjects: true }) || [];
  const projects = t("portfolio.projects", { returnObjects: true }) || [];

  const filteredProjects =
    activeFilter === "all"
      ? projects
      : projects.filter((p) =>
          getProjectCategoryIds(p).includes(activeFilter)
        );

  const renderCategoryIcon = (iconKey) => {
    switch (iconKey) {
      case "globe":
        return <Globe className="portfolio__filter-icon" />;
      case "code":
        return <Code2 className="portfolio__filter-icon" />;
      case "shoppingCart":
        return <ShoppingCart className="portfolio__filter-icon" />;
      case "briefcase":
        return <Briefcase className="portfolio__filter-icon" />;
      case "smartphone":
        return <Smartphone className="portfolio__filter-icon" />;
      default:
        return <Globe className="portfolio__filter-icon" />;
    }
  };

  const getCategoryLabel = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat ? cat.label : categoryId;
  };

  return (
    <div className="portfolio">
      {/* HERO */}
      <section className="portfolio__hero">
        <div className="portfolio__hero-bg">
          <motion.div
            className="portfolio__hero-circle portfolio__hero-circle--blue"
            animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
            transition={{ duration: 15, repeat: Infinity }}
          />
          <motion.div
            className="portfolio__hero-circle portfolio__hero-circle--green"
            animate={{ scale: [1, 1.3, 1], rotate: [0, -90, 0] }}
            transition={{ duration: 12, repeat: Infinity }}
          />
        </div>

        <div className="portfolio__hero-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="portfolio__hero-badge"
          >
            <Code2 className="portfolio__hero-badge-icon" />
            <span className="portfolio__hero-badge-text">
              {t("portfolio.hero.badge")}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="portfolio__hero-title"
          >
            {t("portfolio.hero.titleLine1")}
            <br />
            <span className="portfolio__hero-title-highlight">
              {t("portfolio.hero.titleHighlight")}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="portfolio__hero-subtitle"
          >
            {t("portfolio.hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="portfolio__hero-metrics"
          >
            <div className="portfolio__hero-metric">
              <span className="portfolio__hero-dot portfolio__hero-dot--green" />
              <span className="portfolio__hero-metric-text">
                {t("portfolio.hero.metric1")}
              </span>
            </div>
            <div className="portfolio__hero-metric">
              <span className="portfolio__hero-dot portfolio__hero-dot--blue" />
              <span className="portfolio__hero-metric-text">
                {t("portfolio.hero.metric2")}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="portfolio__filters">
        <div className="portfolio__filters-inner">
          <div className="portfolio__filters-list">
            {categories.map((category, index) => (
              <motion.button
                key={category.id}
                type="button"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => setActiveFilter(category.id)}
                className={
                  "portfolio__filter-btn" +
                  (activeFilter === category.id
                    ? " portfolio__filter-btn--active"
                    : "")
                }
              >
                {renderCategoryIcon(category.icon)}
                <span>{category.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS GRID */}
      <section className="portfolio__grid-section">
        <div className="portfolio__grid-inner">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="portfolio__grid"
            >
              {filteredProjects.length === 0 && (
                <p className="portfolio__no-projects">
                  {t("portfolio.projectsSection.noProjects")}
                </p>
              )}

              {filteredProjects.map((project, index) => {
                const categoryIds = getProjectCategoryIds(project);
                const mainCategoryId = categoryIds[0] || "webapp";

                return (
                  <motion.a
                    key={project.id}
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -8 }}
                    className="portfolio__card"
                  >
                    <div className="portfolio__card-image-wrapper">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="portfolio__card-image"
                      />
                      <div className="portfolio__card-image-gradient" />
                      <div className="portfolio__card-year">
                        {project.year}
                      </div>

                      {/* ✅ Plusieurs pastilles en colonne */}
                      <div className="portfolio__card-categories">
                        {categoryIds.map((catId) => (
                          <span
                            key={catId}
                            className="portfolio__card-category"
                            style={{
                              backgroundColor: getCategoryColor(catId),
                            }}
                          >
                            {getCategoryLabel(catId)}
                          </span>
                        ))}
                      </div>

                      <div className="portfolio__card-hover">
                        <ExternalLink className="portfolio__card-hover-icon" />
                      </div>
                    </div>

                    <div className="portfolio__card-body">
                      <h3 className="portfolio__card-title">
                        {project.title}
                      </h3>
                      <p className="portfolio__card-description">
                        {project.description}
                      </p>

                      <div className="portfolio__card-tech">
                        {project.tech.slice(0, 3).map((tech) => (
                          <span
                            key={tech}
                            className="portfolio__card-tech-tag"
                          >
                            {tech}
                          </span>
                        ))}
                        {project.tech.length > 3 && (
                          <span className="portfolio__card-tech-tag portfolio__card-tech-tag--more">
                            +{project.tech.length - 3}
                          </span>
                        )}
                      </div>

                      <div className="portfolio__card-results">
                        {project.results.slice(0, 2).map((result, idx) => (
                          <div
                            key={idx}
                            className="portfolio__card-result-row"
                          >
                            <span
                              className="portfolio__card-result-dot"
                              style={{
                                backgroundColor: getCategoryColor(
                                  mainCategoryId
                                ),
                              }}
                            />
                            <span className="portfolio__card-result-text">
                              {result}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.a>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* CTA */}
      <section className="portfolio__cta-section">
        <motion.div
          className="portfolio__cta-card"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="portfolio__cta-title">
            {t("portfolio.cta.title")}
          </h2>
          <p className="portfolio__cta-subtitle">
            {t("portfolio.cta.subtitle")}
          </p>
          <Link to={createPageUrl("WebDevelopment") + "#webdev-contact"}>
            <button className="portfolio__cta-btn">
              {t("portfolio.cta.buttonLabel")}
              <ArrowRight className="portfolio__cta-btn-icon" />
            </button>
          </Link>
        </motion.div>
      </section>
    </div>
  );
}

export default Portfolio;
